// Product page behaviour: gallery, zoom, qty, sticky buy bar, warranty-from-spec, related, recently viewed,
// wishlist, copy link. Cards for related / recent / wishlist come from Liquid (pc-card), so quote prices stay hidden.

import { productQuoteText, waUrl } from '@pc/quote';

const RECENT_KEY = 'pc_recent';
const WISH_KEY = 'pc_wishlist';

// Returns the text next to a "אחריות" label in the description's spec table, or null.
export function warrantyFromSpec(root) {
  if (!root) return null;
  for (const cell of root.querySelectorAll('th, td, dt')) {
    if (cell.textContent.trim() !== 'אחריות') continue;
    const next = cell.tagName === 'DT' ? cell.nextElementSibling : cell.nextElementSibling;
    const text = next && next.textContent.trim();
    if (text) return text;
  }
  return null;
}

const txt = (el) => (el ? el.textContent.replace(/\s+/g, ' ').trim() : '');
const el = (tag, attrs = {}, kids = []) => {
  const n = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) (k === 'text' ? (n.textContent = v) : n.setAttribute(k, v));
  for (const c of kids) n.append(c);
  return n;
};

// "Three-year warranty with 9×5 next business day (NBD)" → Hebrew, as in the mockup. Unknown wording stays as-is.
export function warrantyHebrew(s) {
  if (!s) return null;
  const years = /(\d+|one|two|three|four|five)[\s-]*(year|yr)/i.exec(s);
  const months = /(\d+)[\s-]*months?/i.exec(s);
  const map = { one: 1, two: 2, three: 3, four: 4, five: 5 };
  let n = years ? (map[years[1].toLowerCase()] || years[1]) : null;
  if (!n && months) {
    const m = Number(months[1]);
    if (!m) return null; // "0 months" = no warranty stated: show nothing rather than a raw "months 0"
    if (m % 12) return { title: `אחריות יצרן ל-${m} חודשים`, sub: /NBD|next business day/i.test(s) ? 'שירות ביום העסקים הבא (NBD)' : '' };
    n = m / 12;
  }
  const nbd = /NBD|next business day/i.test(s);
  if (!n && !nbd) return { title: s, sub: '' };
  return { title: n ? `אחריות יצרן ל-${n} שנים` : 'אחריות יצרן', sub: nbd ? 'שירות ביום העסקים הבא (NBD)' : '' };
}

// Re-lay the scrapers' description blocks like mockups/product.html. Returns true when it did.
export function restructureDescription(prose, mount) {
  if (!prose || !mount) return false;
  const heads = [...prose.querySelectorAll('h2, h3')];
  const find = (re) => heads.find((h) => re.test(txt(h)));
  const hAbout = find(/תיאור המוצר/), hFor = find(/למי זה מתאים/), hFaq = find(/שאלות ותשובות/), hSpec = find(/מפרט טכני/);
  const table = hSpec && hSpec.parentElement.querySelector('table');
  if (!table) return false; // not the scraper shape: keep the styled prose

  // spec table → two-column definition grid, first 8 rows, "show full spec"
  // header rows ("Attribute name | Attribute value") are not specs
  const rows = [...table.querySelectorAll('tr')].filter((tr) => [...tr.children].some((c) => c.tagName === 'TD'))
    .map((tr) => [...tr.children].map(txt)).filter((r) => r.length >= 2 && r[0] && r[1] && !/^attribute name$/i.test(r[0]));
  const tbl = el('div', { class: 'pc-tbl' }, rows.map(([k, v], i) => el('dl', i >= 8 ? { class: 'is-more' } : {}, [el('dt', { text: k }), el('dd', { text: v, dir: 'auto' })])));
  const spec = el('section', { class: 'pc-specs', 'aria-labelledby': 'pc-spec-t' }, [el('h2', { class: 'pc-t3', id: 'pc-spec-t', text: 'מפרט טכני' }), tbl]);
  if (rows.length > 8) {
    const more = el('button', { type: 'button', class: 'pc-more-btn', 'aria-expanded': 'false', text: 'הצגת המפרט המלא' });
    more.addEventListener('click', () => {
      const open = tbl.classList.toggle('is-open');
      more.textContent = open ? 'הצגת פחות' : 'הצגת המפרט המלא';
      more.setAttribute('aria-expanded', String(open));
    });
    spec.append(more);
  }
  mount.append(spec);

  // about + who it's for, side by side
  const aboutText = hAbout ? txt(hAbout.nextElementSibling) : '';
  const forItems = hFor ? [...hFor.parentElement.querySelectorAll('li')].map(txt).filter(Boolean) : [];
  if (aboutText || forItems.length) {
    const about = el('section', { class: 'pc-about' });
    if (aboutText) about.append(el('div', {}, [el('h3', { text: 'על המוצר' }), el('p', { text: aboutText, dir: 'auto' })]));
    if (forItems.length) about.append(el('div', {}, [el('h3', { text: 'למי זה מתאים' }), el('ul', {}, forItems.map((t) => el('li', { text: t })))]));
    mount.append(about);
  }

  // FAQ: "ש:" / "ת:" pairs → accordion
  if (hFaq) {
    const items = [...hFaq.parentElement.children].filter((c) => c !== hFaq && c.querySelectorAll('p').length >= 2);
    const qa = items.map((it) => {
      const [q, a] = [...it.querySelectorAll('p')];
      return [txt(q).replace(/^ש:\s*/, ''), txt(a).replace(/^ת:\s*/, '')];
    }).filter(([q, a]) => q && a);
    if (qa.length) {
      const faq = el('section', { class: 'pc-faq', 'aria-labelledby': 'pc-faq-t' }, [el('h2', { class: 'pc-t3', id: 'pc-faq-t', text: 'שאלות נפוצות' })]);
      for (const [q, a] of qa) faq.append(el('details', {}, [el('summary', {}, [el('span', { text: q, dir: 'auto' }), el('i', { 'aria-hidden': 'true', text: '+' })]), el('p', { text: a, dir: 'auto' })]));
      mount.append(faq);
    }
  }
  mount.hidden = false;
  return true;
}

function readList(key) {
  try { return JSON.parse(localStorage.getItem(key)) || []; } catch { return []; }
}
function writeList(key, list) {
  try { localStorage.setItem(key, JSON.stringify(list)); } catch { /* private mode: feature just doesn't persist */ }
}

async function cards(handles) {
  const html = await Promise.all(handles.map((h) =>
    fetch(`/products/${encodeURIComponent(h)}?view=card`).then((r) => (r.ok ? r.text() : '')).catch(() => '')));
  return html.join('');
}

function init() {
  const root = document.querySelector('[data-pc-product]');
  if (!root) return;
  const handle = root.dataset.handle;
  const live = root.querySelector('[data-pc-live]');
  const say = (msg) => { if (live) { live.textContent = ''; requestAnimationFrame(() => { live.textContent = msg; }); } };

  // gallery thumbs + zoom dialog
  const main = root.querySelector('[data-pc-main]');
  const thumbs = [...root.querySelectorAll('[data-pc-thumb]')];
  const showPic = (t) => {
    // the first picture carries a srcset; drop it so the chosen picture's src is the one shown
    if (main) { main.removeAttribute('srcset'); main.src = t.dataset.pcThumb; }
    thumbs.forEach((o) => o.setAttribute('aria-current', String(o === t)));
  };
  thumbs.forEach((t) => t.addEventListener('click', () => showPic(t)));
  // swipe the main image to the next/previous picture (RTL: the next one comes in from the left)
  if (main && thumbs.length > 1) {
    let x0 = null, y0 = 0;
    main.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; }, { passive: true });
    main.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0, dy = e.changedTouches[0].clientY - y0;
      x0 = null;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      const i = Math.max(0, thumbs.findIndex((o) => o.getAttribute('aria-current') === 'true'));
      const step = (dx > 0) === (document.dir === 'rtl' || document.documentElement.dir === 'rtl') ? 1 : -1;
      showPic(thumbs[(i + step + thumbs.length) % thumbs.length]);
    });
  }
  const dlg = document.querySelector('[data-pc-zoom-dialog]');
  root.querySelector('[data-pc-zoom]')?.addEventListener('click', () => {
    dlg.querySelector('[data-pc-zoom-img]').src = main.src.replace(/width=\d+/, 'width=2000');
    dlg.showModal();
  });
  dlg?.querySelector('[data-pc-zoom-close]').addEventListener('click', () => dlg.close());
  dlg?.addEventListener('click', (e) => { if (e.target === dlg) dlg.close(); });

  // quantity steppers: click (works for keyboard Enter/Space too); :active gives the press feedback
  root.querySelectorAll('[data-pc-qty]').forEach((q) => {
    const input = q.querySelector('input');
    q.querySelectorAll('[data-pc-qty-step]').forEach((b) => b.addEventListener('click', () => {
      input.value = Math.max(1, (parseInt(input.value, 10) || 1) + Number(b.dataset.pcQtyStep));
      input.dispatchEvent(new Event('input', { bubbles: true })); // clears a shown cart error
    }));
  });

  // variant picker (every state with a real choice): the SKU line, the price (never in quote state: those
  // options carry no data-price), the ?variant= URL and every WhatsApp link — main row and sticky bar — follow it
  const picker = root.querySelector('[data-pc-variant]');
  // Per-variant quote rule: an option at ₪0 / ≥ ₪15,000 carries data-quote="1" and no price (the server never
  // prints it). Choosing one hides the price and the cart buttons (main form + sticky bar) and shows the quote
  // WhatsApp CTA in the same slot; a normal option brings the cart back. Products that are quote as a whole
  // have no data-quote options and no cart buttons, so this does nothing there.
  const isQuoteOpt = (o) => !!o && o.dataset.quote === '1';
  const setQuote = (q) => {
    document.querySelectorAll('[data-pc-price-quote]').forEach((n) => { n.hidden = !q; });
    document.querySelectorAll('[data-pc-price-normal]').forEach((n) => { n.hidden = q; });
    const swapped = [];
    document.querySelectorAll('[data-pc-cart-cta]').forEach((b) => {
      if (q && b === document.activeElement) swapped.push(b);
      b.hidden = q;
      b.disabled = q;
    });
    document.querySelectorAll('[data-pc-quote-cta]').forEach((a) => { a.hidden = !q; });
    if (q) {
      const was = root.querySelector('.pc-buy__was');
      if (was) { was.textContent = ''; was.hidden = true; }
      root.querySelector('[data-pc-more]')?.setAttribute('hidden', '');
      root.querySelector('[data-pc-cart-error]')?.setAttribute('hidden', '');
      // keyboard focus was on a button that just went away: hand it to the quote CTA in the same slot
      for (const b of swapped) b.parentElement.querySelector('[data-pc-quote-cta]')?.focus({ preventScroll: true });
    }
  };
  if (picker) {
    const waLinks = () => [...root.querySelectorAll('[data-pc-wa], [data-pc-notify]'),
      ...document.querySelectorAll('[data-pc-buybar] [data-pc-wa], [data-pc-buybar] [data-pc-notify]')];
    const onVariant = () => {
      const opt = picker.selectedOptions[0];
      if (!opt) return;
      const id = opt.value;
      const sku = opt.dataset.sku || '';
      const multi = picker.options.length > 1;
      const title = multi ? `${picker.dataset.title} — ${opt.dataset.vtitle}` : picker.dataset.title;
      const url = multi ? `${picker.dataset.url}?variant=${id}` : picker.dataset.url;
      const skuBox = root.querySelector('.pc-buy__sku');
      if (skuBox) {
        skuBox.querySelector('[data-pc-sku]').textContent = sku;
        skuBox.hidden = !sku;
      }
      const quote = isQuoteOpt(opt);
      const wasQuote = !!document.querySelector('[data-pc-quote-cta]:not([hidden])');
      setQuote(quote);
      if (quote && !wasQuote) say('המחיר לגרסה זו לפי הצעת מחיר');
      if (!quote && opt.dataset.price) {
        document.querySelectorAll('.pc-buy__price .pc-price, .pc-buybar__p .pc-price').forEach((p) => { p.textContent = opt.dataset.price; });
        const was = root.querySelector('.pc-buy__was');
        if (was) { was.textContent = opt.dataset.was || ''; was.hidden = !opt.dataset.was; }
      }
      for (const a of waLinks()) {
        a.dataset.title = title;
        a.dataset.sku = sku;
        a.dataset.url = url;
        if (a.hasAttribute('data-pc-notify')) {
          a.href = waUrl(`שלום, אשמח לקבל עדכון כשהמוצר: ${title}${sku ? ` (מק"ט ${sku})` : ''} יחזור למלאי - ${url}`);
        } else {
          a.dispatchEvent(new Event('pc:wa-refresh'));
        }
      }
      const u = new URL(location.href);
      u.searchParams.set('variant', id);
      history.replaceState(history.state, '', u);
    };
    picker.addEventListener('change', onVariant);
    // a back/forward restore can bring back another option than the server rendered
    if (picker.selectedOptions[0] && !picker.selectedOptions[0].defaultSelected) onVariant();
  }

  // sticky buy bar: shows once the main action row leaves the screen under the header
  const bar = document.querySelector('[data-pc-buybar]');
  const row = root.querySelector('[data-pc-buyrow]');
  if (bar && row) {
    const headerH = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--pc-header-h'), 10) || 64;
    new IntersectionObserver(([e]) => {
      const on = !e.isIntersecting && e.boundingClientRect.top < 0;
      bar.classList.toggle('is-on', on);
      bar.setAttribute('aria-hidden', String(!on));
      bar.querySelectorAll('a, button').forEach((el) => { el.tabIndex = on ? 0 : -1; });
    }, { rootMargin: `-${headerH}px 0px 0px 0px` }).observe(row);
    bar.querySelector('[data-pc-buybar-submit]')?.addEventListener('click', () => root.querySelector('[data-pc-form]')?.requestSubmit());
  }

  // warranty: metafield wins; otherwise read the spec table; shown in Hebrew like the mockup; else hidden
  const w = root.querySelector('[data-pc-warranty]');
  if (w) {
    const raw = w.hidden ? warrantyFromSpec(document.querySelector('[data-pc-prose]')) : w.querySelector('[data-pc-warranty-text]').textContent.trim();
    const he = warrantyHebrew(raw);
    if (he) {
      w.querySelector('[data-pc-warranty-title]').textContent = he.title;
      w.querySelector('[data-pc-warranty-text]').textContent = he.sub;
      w.hidden = false;
    }
  }

  // summary under the title: the description's own Hebrew first sentence (as in the mockup), not the English spec string
  const sum = root.querySelector('.pc-buy__sum');
  const intro = document.querySelector('[data-pc-prose] > div:first-child');
  if (sum && intro) {
    const first = txt(intro).split(/(?<=\.)\s/)[0];
    if (/[\u0590-\u05FF]/.test(first) && first.length <= 220) sum.textContent = first;
  }

  // description: re-lay out like the mockup when it has the scrapers' shape
  const structured = document.querySelector('[data-pc-structured]');
  if (restructureDescription(document.querySelector('[data-pc-prose]'), structured)) {
    document.querySelector('[data-pc-desc]').hidden = true;
  }

  // add to cart without leaving the page: the plain form POST reloaded the page and landed on /cart.
  // The cart-drawer listens for CartLinesUpdateEvent and re-renders itself; the header count follows it.
  const form = root.querySelector('[data-pc-form]');
  let CartLinesUpdateEvent = null; // loaded up front: preventDefault() must run before any await
  import('@shopify/events').then((m) => { CartLinesUpdateEvent = m.CartLinesUpdateEvent; }).catch(() => {});
  // both the main button and the sticky bar's button show the state: the main one is off-screen when the bar is used
  const btns = [form?.querySelector('[type=submit]'), document.querySelector('[data-pc-buybar-submit]')].filter(Boolean);
  const labels = btns.map((b) => b.textContent);
  const errBox = root.querySelector('[data-pc-cart-error]');
  const show = (text) => btns.forEach((b, i) => { b.textContent = text ?? labels[i]; });
  let resetTimer;
  // quantity above stock: what went into the cart + a WhatsApp quote for the whole amount
  const more = root.querySelector('[data-pc-more]');
  const showMore = (inCart, wanted) => {
    if (!more) { if (errBox) { errBox.textContent = 'לכמות כזו נכין לכם הצעת מחיר מסודרת. כתבו לנו בוואטסאפ.'; errBox.hidden = false; } return; }
    const opt = picker?.options.length > 1 ? picker.selectedOptions[0] : null;
    const base = root.querySelector('.pc-buy__title')?.textContent.trim() || document.title;
    const title = opt ? `${base} — ${opt.dataset.vtitle}` : base;
    const sku = root.querySelector('.pc-buy__sku .lat')?.textContent.trim() || '';
    const url = location.origin + location.pathname + (opt ? `?variant=${opt.value}` : '');
    more.querySelector('[data-pc-more-text]').textContent = inCart
      ? `בסל יש עכשיו ${inCart === 1 ? 'יחידה אחת' : `${inCart} יחידות`}. לכמות של ${wanted} יחידות נכין לכם הצעת מחיר מסודרת.`
      : `לכמות של ${wanted} יחידות נכין לכם הצעת מחיר מסודרת, כולל זמן אספקה.`;
    const a = more.querySelector('[data-pc-more-wa]');
    a.href = waUrl(productQuoteText({ title, sku, qty: wanted, url }));
    a.textContent = `הצעת מחיר ל-${wanted} יחידות בוואטסאפ`;
    more.hidden = false;
    if (more.getBoundingClientRect().bottom < 0 || more.getBoundingClientRect().top > innerHeight) more.scrollIntoView({ block: 'center' });
    say(more.querySelector('[data-pc-more-text]').textContent);
  };
  form?.querySelector('input[name=quantity]')?.addEventListener('input', () => { if (more) more.hidden = true; });
  form?.addEventListener('submit', async (e) => {
    // cart guard: a quote-priced variant never goes in the cart, whatever path submitted it (Enter, the bar,
    // a stale back/forward form): stop it here, before both the AJAX add and the plain POST
    const quoteId = String(new FormData(form).get('id'));
    if (picker && isQuoteOpt([...picker.options].find((o) => o.value === quoteId))) {
      e.preventDefault();
      setQuote(true);
      say('המחיר לגרסה זו לפי הצעת מחיר. שלחו לנו בקשה בוואטסאפ.');
      return;
    }
    if (!CartLinesUpdateEvent) return; // events module not loaded (yet): the normal POST still works
    e.preventDefault();
    if (form.getAttribute('aria-busy') === 'true') return;
    clearTimeout(resetTimer);
    form.setAttribute('aria-busy', 'true');
    btns.forEach((b) => b.setAttribute('aria-busy', 'true'));
    show('מוסיף…');
    if (errBox) errBox.hidden = true;
    if (more) more.hidden = true;
    const data = new FormData(form);
    const ids = [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);
    if (ids.length) data.append('sections', ids.join(','));
    const deferred = CartLinesUpdateEvent.createPromise();
    form.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add', context: 'product', promise: deferred.promise,
      lines: [{ merchandiseId: String(data.get('id')), quantity: Number(data.get('quantity')) || 1 }],
    }));
    let ok = false;
    const fromBar = document.activeElement?.matches?.('[data-pc-buybar-submit]');
    const variantId = Number(data.get('id'));
    const getCart = () => fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    try {
      const res = await fetch('/cart/add.js', { method: 'POST', body: data, headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } });
      const added = await res.json().catch(() => ({}));
      if (!res.ok || added.status) {
        if (res.status === 422) {
          // a 422 can still add what's in stock ("Only 5 items were added…"): show the real cart, say how many are in it
          const cart = await getCart();
          deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { items: cart.items, source: 'pc-product', didError: false } });
          // more than the shelf holds is a sale, not an error: offer a quote for the full quantity
          const inCart = cart.items.filter((i) => i.variant_id === variantId).reduce((n, i) => n + i.quantity, 0);
          const wanted = Number(data.get('quantity')) || 1;
          show(null);
          showMore(inCart, wanted);
          return;
        }
        throw new Error('לא הצלחנו להוסיף לסל. נסו שוב, או כתבו לנו בוואטסאפ.');
      }
      const cart = await getCart();
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, sections: added.sections, source: 'pc-product', didError: false } });
      ok = true;
      show('נוסף לסל ✓');
      say('המוצר נוסף לסל');
    } catch (err) {
      deferred.resolve({ detail: { didError: true } }); // no-op when the 422 branch already resolved it
      const msg = err instanceof TypeError ? 'אין חיבור לאינטרנט. נסו שוב.' : err.message;
      show(null);
      if (errBox) { errBox.textContent = msg; errBox.hidden = false; } else say(msg);
      if (errBox && errBox.getBoundingClientRect().bottom < 0) errBox.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      // the bar hides once the buy row is back in view: don't leave keyboard focus on a hidden button
      if (fromBar) form.querySelector('input[name=quantity]')?.focus({ preventScroll: true });
    } finally {
      form.removeAttribute('aria-busy');
      btns.forEach((b) => b.removeAttribute('aria-busy'));
      if (ok) resetTimer = setTimeout(() => show(null), 2000);
    }
  });
  form?.addEventListener('input', () => { if (errBox) errBox.hidden = true; });

  // "שאלה על התצורה" opens the bot (assets/pc-bot.js defines window.pcOpenBot)
  root.querySelector('[data-pc-open-bot]')?.addEventListener('click', () => window.pcOpenBot?.());

  // copy link
  root.querySelector('[data-pc-copy]')?.addEventListener('click', async (e) => {
    try { await navigator.clipboard.writeText(e.currentTarget.dataset.pcCopy); say('הקישור הועתק'); } catch { say('לא ניתן להעתיק'); }
  });

  // wishlist
  const wish = root.querySelector('[data-pc-wish]');
  if (wish) {
    const sync = () => wish.setAttribute('aria-pressed', String(readList(WISH_KEY).includes(handle)));
    sync();
    wish.addEventListener('click', () => {
      const list = readList(WISH_KEY);
      const on = !list.includes(handle);
      writeList(WISH_KEY, on ? [handle, ...list].slice(0, 50) : list.filter((h) => h !== handle));
      sync();
      say(on ? 'נוסף לרשימה' : 'הוסר מהרשימה');
    });
  }

  // related products (Section Rendering API → pc-card)
  const rel = document.querySelector('[data-pc-related]');
  if (rel) {
    fetch(rel.dataset.url).then((r) => r.text()).then((html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const items = doc.querySelectorAll('.pc-card');
      if (items.length) { rel.querySelector('[data-pc-related-grid]').append(...items); rel.hidden = false; }
    }).catch(() => {});
  }

  // recently viewed: show up to 8 others, then record this one
  const recent = readList(RECENT_KEY).filter((h) => h !== handle).slice(0, 8);
  const rv = document.querySelector('[data-pc-recent]');
  if (rv && recent.length) {
    cards(recent.slice(0, 4)).then((html) => {
      if (!html.trim()) return;
      rv.querySelector('[data-pc-recent-grid]').innerHTML = html;
      rv.hidden = false;
    });
  }
  writeList(RECENT_KEY, [handle, ...recent].slice(0, 8));
}

if (typeof document !== "undefined") init();
